import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '../Context/AuthContext'
import { endSession, getAuthToken } from '../services/apiConfig'
import {
  IDLE_TIMEOUT_MS,
  WARNING_MS,
  getLastActivity,
  getTokenExpiry,
  recordActivity,
  refreshSession,
  shouldRefresh,
} from '../services/session'
import './SessionTimeout.css'

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'wheel']
const ACTIVITY_THROTTLE_MS = 15 * 1000
const CHECK_EVERY_MS = 5 * 1000

const formatCountdown = (ms) => {
  const seconds = Math.max(0, Math.ceil(ms / 1000))
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

/**
 * Signs the user out after IDLE_TIMEOUT_MS without activity (warning WARNING_MS before),
 * keeps an active user's short-lived token fresh, and follows sign-outs from other tabs.
 * Renders nothing unless the inactivity warning is showing.
 */
function SessionTimeout() {
  const { user } = useAuth()
  const [remainingMs, setRemainingMs] = useState(null)
  const lastWrite = useRef(0)

  const markActive = useCallback(() => {
    const now = Date.now()
    if (now - lastWrite.current < ACTIVITY_THROTTLE_MS) return
    lastWrite.current = now
    recordActivity(now)
  }, [])

  // Track activity while signed in
  useEffect(() => {
    if (!user) return undefined
    if (!getLastActivity()) recordActivity()
    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, markActive, { passive: true }))
    return () => ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, markActive))
  }, [user, markActive])

  // Periodic check: sign out when idle or expired, warn shortly before, refresh while active
  useEffect(() => {
    if (!user) {
      setRemainingMs(null)
      return undefined
    }

    const check = () => {
      const now = Date.now()
      if (!getAuthToken()) return endSession('signedout') // signed out in another tab

      const idleLeft = IDLE_TIMEOUT_MS - (now - getLastActivity())
      if (idleLeft <= 0) return endSession('idle')

      const expiry = getTokenExpiry()
      if (expiry !== null && expiry <= now) return endSession('expired')

      setRemainingMs(idleLeft <= WARNING_MS ? idleLeft : null)
      if (shouldRefresh(now)) refreshSession()
      return undefined
    }

    check()
    const timer = setInterval(check, CHECK_EVERY_MS)
    // Another tab signed out or refreshed: re-check right away
    const onStorage = (event) => {
      if (['token', 'lastActivity'].includes(event.key)) check()
    }
    window.addEventListener('storage', onStorage)
    return () => {
      clearInterval(timer)
      window.removeEventListener('storage', onStorage)
    }
  }, [user])

  // Tick the countdown every second while the warning is visible
  useEffect(() => {
    if (remainingMs === null) return undefined
    const tick = setInterval(() => {
      const left = IDLE_TIMEOUT_MS - (Date.now() - getLastActivity())
      if (left <= 0) {
        endSession('idle')
      } else {
        setRemainingMs(left <= WARNING_MS ? left : null)
      }
    }, 1000)
    return () => clearInterval(tick)
  }, [remainingMs === null]) // eslint-disable-line react-hooks/exhaustive-deps

  const staySignedIn = async () => {
    lastWrite.current = Date.now()
    recordActivity()
    setRemainingMs(null)
    await refreshSession()
  }

  if (!user || remainingMs === null) return null

  return (
    <div className="session-timeout" role="presentation">
      <div
        className="session-timeout__dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="session-timeout-title"
        aria-describedby="session-timeout-text"
      >
        <span className="session-timeout__icon" aria-hidden="true">
          <i className="fa-solid fa-user-clock" />
        </span>
        <h2 id="session-timeout-title">Are you still there?</h2>
        <p id="session-timeout-text">
          For your security, you&apos;ll be signed out in{' '}
          <strong className="session-timeout__count">{formatCountdown(remainingMs)}</strong> because you haven&apos;t
          been active.
        </p>
        <div className="session-timeout__actions">
          <button type="button" className="session-timeout__btn session-timeout__btn--primary" onClick={staySignedIn} autoFocus>
            Stay signed in
          </button>
          <button type="button" className="session-timeout__btn" onClick={() => endSession('signedout')}>
            Sign out
          </button>
        </div>
      </div>
    </div>
  )
}

export default SessionTimeout
